import React from "react";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

const contractTypes = [
  "Service Agreement",
  "Non-Disclosure Agreement",
  "Employment Contract",
  "Vendor Agreement",
  "Licensing Agreement",
  "Consulting Agreement",
  "Partnership Agreement",
  "Sales Contract",
  "Lease Agreement",
  "Other",
];

const spendCategories = [
  "IT & Technology", "Professional Services", "Facilities & Maintenance",
  "Marketing & Advertising", "HR & Recruitment", "Legal Services",
  "Logistics & Transport", "Raw Materials", "Office Supplies",
  "Utilities & Energy", "Insurance", "Consulting",
  "Outsourcing & BPO", "Training & Development", "Travel & Expenses",
  "Capital Expenditure", "R&D", "Licensing & Subscriptions", "Other",
];

const CURRENCIES = [
  "GBP", "USD", "EUR", "AED", "AUD", "BHD", "BRL", "CAD", "CHF", "CNY",
  "DKK", "EGP", "HKD", "IDR", "INR", "JPY", "KES", "KRW", "KWD", "MXN",
  "MYR", "NGN", "NOK", "NZD", "OMR", "PHP", "PLN", "QAR", "SAR", "SEK",
  "SGD", "THB", "TRY", "VND", "ZAR",
];

import { governingLawOptions } from "../templates/templateData";

const jurisdictions = governingLawOptions;

export function EditorSidebar({ formData, isEditing, onChange }) {
  return (
    <div className="space-y-6">
      {/* Basic Info */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-mono uppercase tracking-wider text-slate-500">
            Contract Details
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {isEditing && formData.contract_unique_id && (
            <div className="space-y-2">
              <Label className="text-xs font-mono uppercase tracking-wider text-slate-500">
                Contract ID
              </Label>
              <Input
                value={formData.contract_unique_id}
                className="rounded-sm bg-slate-50 dark:bg-slate-800 font-mono"
                disabled
                data-testid="contract-unique-id-display"
              />
            </div>
          )}
          {!isEditing && (
            <p className="text-xs text-slate-400">A unique Contract ID will be auto-generated on save.</p>
          )}
          <div className="space-y-2">
            <Label className="text-xs font-mono uppercase tracking-wider text-slate-500">
              Contract Title *
            </Label>
            <Input
              value={formData.title}
              onChange={(e) => onChange("title", e.target.value)}
              placeholder="e.g., Service Agreement with Acme Corp"
              className="rounded-sm"
              data-testid="contract-title-input"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-xs font-mono uppercase tracking-wider text-slate-500">
              Contract Type
            </Label>
            <Select
              value={formData.contract_type}
              onValueChange={(value) => onChange("contract_type", value)}
            >
              <SelectTrigger className="rounded-sm" data-testid="contract-type-select">
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                {contractTypes.map((type) => (
                  <SelectItem key={type} value={type}>{type}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="text-xs font-mono uppercase tracking-wider text-slate-500">
              Contract Value
            </Label>
            <div className="flex gap-2">
              <Select
                value={formData.currency || ""}
                onValueChange={(value) => onChange("currency", value)}
              >
                <SelectTrigger className="rounded-sm w-24" data-testid="contract-currency-select">
                  <SelectValue placeholder="Ccy *" />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  {CURRENCIES.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                type="number"
                value={formData.contract_value}
                onChange={(e) => onChange("contract_value", e.target.value)}
                placeholder="0.00"
                className="rounded-sm flex-1"
                data-testid="contract-value-input"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-xs font-mono uppercase tracking-wider text-slate-500">
              Department
            </Label>
            <Input
              value={formData.department}
              onChange={(e) => onChange("department", e.target.value)}
              placeholder="e.g., Legal, Finance"
              className="rounded-sm"
              data-testid="department-input"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-xs font-mono uppercase tracking-wider text-slate-500">
              Category of Spend
            </Label>
            <Select
              value={formData.spend_category}
              onValueChange={(value) => onChange("spend_category", value)}
            >
              <SelectTrigger className="rounded-sm" data-testid="spend-category-select">
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                {spendCategories.map((cat) => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="text-xs font-mono uppercase tracking-wider text-slate-500">
              Jurisdiction
            </Label>
            <Select
              value={formData.jurisdiction}
              onValueChange={(value) => onChange("jurisdiction", value)}
            >
              <SelectTrigger className="rounded-sm" data-testid="jurisdiction-select">
                <SelectValue placeholder="Select jurisdiction" />
              </SelectTrigger>
              <SelectContent>
                {jurisdictions.map((j) => (
                  <SelectItem key={j} value={j}>{j}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Contract Owner */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-mono uppercase tracking-wider text-slate-500">
            Contract Owner
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label className="text-xs font-mono uppercase tracking-wider text-slate-500">
              Owner Name
            </Label>
            <Input
              value={formData.contract_owner_name}
              onChange={(e) => onChange("contract_owner_name", e.target.value)}
              placeholder="Full name"
              className="rounded-sm"
              data-testid="owner-name-input"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-mono uppercase tracking-wider text-slate-500">
              Owner Email
            </Label>
            <Input
              type="email"
              value={formData.contract_owner_email}
              onChange={(e) => onChange("contract_owner_email", e.target.value)}
              placeholder="owner@company.com"
              className="rounded-sm"
              data-testid="owner-email-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Dates */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-mono uppercase tracking-wider text-slate-500">
            Key Dates
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label className="text-xs font-mono uppercase tracking-wider text-slate-500">
              Effective Date
            </Label>
            <Input
              type="date"
              value={formData.effective_date}
              onChange={(e) => onChange("effective_date", e.target.value)}
              className="rounded-sm"
              data-testid="effective-date-input"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-mono uppercase tracking-wider text-slate-500">
              Expiration Date
            </Label>
            <Input
              type="date"
              value={formData.expiration_date}
              onChange={(e) => onChange("expiration_date", e.target.value)}
              className="rounded-sm"
              data-testid="expiration-date-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Supplier */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-mono uppercase tracking-wider text-slate-500">
            Supplier
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label className="text-xs font-mono uppercase tracking-wider text-slate-500">
              Supplier ID
            </Label>
            <Input
              value={formData.supplier_unique_id}
              onChange={(e) => onChange("supplier_unique_id", e.target.value)}
              placeholder="e.g., SUP-001"
              className="rounded-sm"
              data-testid="supplier-id-input"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-mono uppercase tracking-wider text-slate-500">
              Legal Name
            </Label>
            <Input
              value={formData.contracting_party.legal_name}
              onChange={(e) => onChange("contracting_party.legal_name", e.target.value)}
              placeholder="Supplier Legal Name"
              className="rounded-sm"
              data-testid="party-legal-name-input"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-mono uppercase tracking-wider text-slate-500">
              Registered Address
            </Label>
            <Textarea
              value={formData.contracting_party.registered_address}
              onChange={(e) => onChange("contracting_party.registered_address", e.target.value)}
              placeholder="Full address"
              rows={2}
              className="rounded-sm"
              data-testid="party-address-input"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-mono uppercase tracking-wider text-slate-500">
              Contact Email
            </Label>
            <Input
              type="email"
              value={formData.contracting_party.contact_email}
              onChange={(e) => onChange("contracting_party.contact_email", e.target.value)}
              placeholder="contact@supplier.com"
              className="rounded-sm"
              data-testid="party-email-input"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-mono uppercase tracking-wider text-slate-500">
              Contact Phone
            </Label>
            <Input
              value={formData.contracting_party.contact_phone}
              onChange={(e) => onChange("contracting_party.contact_phone", e.target.value)}
              placeholder="+1 (555) 000-0000"
              className="rounded-sm"
              data-testid="party-phone-input"
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
